import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Maximize2, X, Download, ExternalLink } from "lucide-react";
import { useClient } from "@/contexts/ClientContext";
import { NativeMenuRenderer } from "@/components/NativeMenuRenderer";

export interface MenuConfig {
  type: "image" | "pdf" | "url" | "native";
  value: string;
  value_mobile?: string;
  label?: string;
  orientation?: "landscape" | "portrait";
}

interface DigitalMenuViewerProps {
  menuConfig: MenuConfig;
  /** When true, render the menu inline (no button, no dialog). */
  inline?: boolean;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : true
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

export const DigitalMenuViewer = ({ menuConfig, inline = false }: DigitalMenuViewerProps) => {
  const { client, clientSettings } = useClient();
  const [open, setOpen] = useState(false);
  const [imgZoomed, setImgZoomed] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const primary = clientSettings?.brand_primary_color ?? "#6366f1";

  const buttonLabel = menuConfig.label || "Consulter la carte";
  const emoji =
    menuConfig.type === "image" ? "🍽️"
      : menuConfig.type === "pdf" ? "📄"
      : menuConfig.type === "native" ? "🍽️"
      : "🔗";

  // Use mobile-optimized image when available on small viewports
  const effectiveValue =
    menuConfig.type === "image" && isMobile && menuConfig.value_mobile
      ? menuConfig.value_mobile
      : menuConfig.value;

  const handleFullscreen = () => {
    if (contentRef.current) {
      contentRef.current.requestFullscreen?.();
    }
  };

  const getDialogHeight = () => {
    if (menuConfig.type === "url") return "h-[90dvh]";
    if (menuConfig.type === "pdf") return "h-[85dvh]";
    return "max-h-[85dvh]";
  };

  // INLINE rendering: full-page menu without dialog, used on Index when menu_config is set
  if (inline) {
    return (
      <div className="w-full">
        {menuConfig.type === "image" && (
          <img
            src={effectiveValue}
            alt={buttonLabel}
            className="w-full h-auto rounded-2xl shadow-md"
            loading="lazy"
          />
        )}
        {menuConfig.type === "pdf" && (
          <div className="w-full rounded-2xl overflow-hidden border border-border/50 bg-card">
            {!pdfError ? (
              <embed
                src={effectiveValue}
                type="application/pdf"
                width="100%"
                className="w-full h-[75dvh]"
                onError={() => setPdfError(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
                <p className="text-muted-foreground text-sm">
                  Votre navigateur ne peut pas afficher ce PDF directement.
                </p>
                <a href={effectiveValue} target="_blank" rel="noopener noreferrer" download>
                  <Button className="gap-2">
                    <Download className="w-4 h-4" />
                    Télécharger le PDF
                  </Button>
                </a>
              </div>
            )}
          </div>
        )}
        {menuConfig.type === "url" && (
          <div className="w-full rounded-2xl overflow-hidden border border-border/50 bg-card">
            <iframe
              src={effectiveValue}
              title={buttonLabel}
              className="w-full h-[75dvh] border-0"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          </div>
        )}
        {menuConfig.type === "native" && client?.id && (
          <div className="w-full rounded-2xl border border-border/50 bg-card/40 p-4">
            <NativeMenuRenderer clientId={client.id} />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        className="w-full h-12 gap-2 text-sm font-medium"
        style={{ borderColor: primary, color: primary }}
        onClick={() => setOpen(true)}
      >
        <span>{emoji}</span>
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={`w-full max-w-2xl mx-2 p-0 flex flex-col gap-0 ${getDialogHeight()} overflow-hidden rounded-3xl [&>button]:hidden`}
        >
          {/* Header */}
          <DialogHeader className="flex flex-row items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
            <DialogTitle className="text-base font-semibold">
              {emoji} {buttonLabel}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {menuConfig.type !== "image" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleFullscreen}
                  className="h-8 w-8 p-0"
                  title="Plein écran"
                  aria-label="Afficher en plein écran"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              )}
              {menuConfig.type === "pdf" && (
                <a href={effectiveValue} target="_blank" rel="noopener noreferrer" download>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Télécharger" aria-label="Télécharger le menu">
                    <Download className="w-4 h-4" />
                  </Button>
                </a>
              )}
              {menuConfig.type === "url" && (
                <a href={effectiveValue} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Ouvrir dans le navigateur" aria-label="Ouvrir le menu dans le navigateur">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              )}
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-8 w-8 p-0" aria-label="Fermer">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>

          {/* Content */}
          <div ref={contentRef} className="flex-1 overflow-auto min-h-0">
            {menuConfig.type === "image" && (
              <div
                className="flex items-center justify-center min-h-full p-2 cursor-zoom-in bg-background/50"
                onClick={() => setImgZoomed(!imgZoomed)}
              >
                <img
                  src={effectiveValue}
                  alt={buttonLabel}
                  className={`rounded transition-transform duration-300 ${
                    imgZoomed
                      ? "scale-150 cursor-zoom-out"
                      : "max-w-full max-h-[70dvh] object-contain cursor-zoom-in"
                  }`}
                />
              </div>
            )}

            {menuConfig.type === "pdf" && (
              <div className="w-full h-full min-h-[400px]">
                {!pdfError ? (
                  <embed
                    src={effectiveValue}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    className="min-h-[400px]"
                    onError={() => setPdfError(true)}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
                    <p className="text-muted-foreground text-sm">
                      Votre navigateur ne peut pas afficher ce PDF directement.
                    </p>
                    <a href={effectiveValue} target="_blank" rel="noopener noreferrer" download>
                      <Button className="gap-2">
                        <Download className="w-4 h-4" />
                        Télécharger le PDF
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            )}

            {menuConfig.type === "url" && (
              <div
                className="w-full h-full min-h-[400px]"
                style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
              >
                <iframe
                  src={effectiveValue}
                  title={buttonLabel}
                  width="100%"
                  height="100%"
                  className="border-0 min-h-[400px]"
                  allow="fullscreen"
                  loading="lazy"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              </div>
            )}

            {menuConfig.type === "native" && client?.id && (
              <div className="p-4">
                <NativeMenuRenderer clientId={client.id} />
              </div>
            )}
          </div>

          {menuConfig.type === "image" && (
            <div className="px-4 py-2 border-t border-border/50 shrink-0 flex justify-between items-center">
              <p className="text-xs text-muted-foreground">Appuyez sur l'image pour zoomer</p>
              <Button variant="ghost" size="sm" onClick={handleFullscreen} className="gap-1 h-7 text-xs">
                <Maximize2 className="w-3 h-3" />
                Plein écran
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DigitalMenuViewer;
